// The clock rules for cs-input-time: how an hour moves between the 12-hour
// clock (1-12 plus AM/PM) and the 24-hour clock (0-23).
//
// These used to be written out by hand in four places inside the component,
// and the two copies behind the format switch (the popup menu and the
// on-screen keyboard's unit selector) had drifted apart. One of the drifts
// turned an empty hour box into the text "NaN" and emitted "NaN:30"; the
// other turned it into "00", a midnight the user never entered. Keeping the
// rules here, as plain functions over strings, means every caller gets the
// same answer and the rules can be unit-tested (df-49288ba2b572).

export enum TimeFormat {
  TwelveHourAM = '12h-am',
  TwelveHourPM = '12h-pm',
  TwentyFourHour = '24h'
}

// the hour box text and AM/PM flag that result from a conversion
export interface HourDisplay {
  hourText: string;
  isPM: boolean;
}

// parse an hour box's text. Answers undefined for an empty or non-numeric
// box, so no caller can carry NaN into the arithmetic.
export function parseHourText(hourText: string): number | undefined {
  const hourNum = parseInt(hourText);
  return Number.isNaN(hourNum) ? undefined : hourNum;
}

// write an hour as the two digits the boxes show
function padHour(hourNum: number): string {
  return hourNum.toString().padStart(2, '0');
}

// turn a 12-hour box's text plus its AM/PM flag into an hour of the day, 0-23.
// Midnight is 12 AM and noon is 12 PM. Answers undefined when the box does not
// hold a number.
export function to24hHour(hourText: string, isPM: boolean): number | undefined {
  const hourNum = parseHourText(hourText);
  return hourNum === undefined ? undefined : twelveHourNumTo24h(hourNum, isPM);
}

// the 12-hour-to-24-hour rule on an hour that has already been parsed
function twelveHourNumTo24h(hourNum: number, isPM: boolean): number {
  if (isPM && hourNum < 12) {
    return hourNum + 12;
  }
  if (!isPM && hourNum === 12) {
    return 0;
  }
  return hourNum;
}

// turn an hour of the day, 0-23, into what a 12-hour box shows and its AM/PM flag
export function to12hHour(hour24: number): HourDisplay {
  const isPM = hour24 >= 12;
  if (hour24 === 0) {
    return { hourText: '12', isPM };
  }
  if (hour24 > 12) {
    return { hourText: padHour(hour24 - 12), isPM };
  }
  return { hourText: padHour(hour24), isPM };
}

// the hour box text and AM/PM flag after the user switches the picker from one
// format to another. Both ways of switching (the popup menu and the on-screen
// keyboard) call this, so they cannot disagree.
//
// An empty hour box stays empty: nothing is invented for an hour the user has
// not typed yet. An hour the conversion leaves numerically unchanged keeps the
// box's text exactly as it was, so a partly typed "5" is not rewritten to "05".
export function convertHourForFormat(hourText: string, from: TimeFormat, to: TimeFormat): HourDisplay {
  const hourNum = parseHourText(hourText);

  if (to === TimeFormat.TwentyFourHour) {
    if (hourNum === undefined) {
      return { hourText, isPM: false };
    }
    // if already on the 24-hour clock, the hour is already right
    const hour24 = from === TimeFormat.TwentyFourHour
      ? hourNum
      : twelveHourNumTo24h(hourNum, from === TimeFormat.TwelveHourPM);
    return {
      hourText: hour24 === hourNum ? hourText : padHour(hour24),
      isPM: hour24 >= 12,
    };
  }

  // switching to AM or PM: the user's choice sets the flag, whatever the
  // hour was before
  const isPM = to === TimeFormat.TwelveHourPM;

  // if coming from the 24-hour clock, bring the hour onto the 1-12 dial;
  // between AM and PM the hour itself does not change
  if (from !== TimeFormat.TwentyFourHour || hourNum === undefined) {
    return { hourText, isPM };
  }
  if (hourNum === 0) {
    return { hourText: '12', isPM };
  }
  if (hourNum > 12) {
    return { hourText: padHour(hourNum - 12), isPM };
  }
  return { hourText, isPM };
}
